/**
 * Builds a browser-playable URL for a gs:// survey video.
 *
 * The video is streamed (with Range/seek support) through the server — the
 * bucket can be read but not signed, so a signed URL isn't available. A <video>
 * element can't send an auth header, so the short-lived Firebase ID token is
 * embedded as a query param.
 *
 * Two authorization paths behind the same gs:// uri:
 *  - default → /v1/qa-video, gated on QA reviewer membership;
 *  - pass `learningPropertyId` → /v1/learning/video, which authorizes CLIENTS by
 *    property visibility instead. Learning cards MUST pass it, because clients
 *    aren't QA reviewers and would 403 on /v1/qa-video.
 */
import { useQuery } from '@tanstack/react-query';
import { firebaseAuth } from '@/shared/integrations/firebase/client';

const API_URL = import.meta.env.VITE_QA_API_URL || 'http://localhost:3100';

export function useVideoUrl(
  gsUri: string | null | undefined,
  opts?: { learningPropertyId?: string | null },
) {
  const learningPropertyId = opts?.learningPropertyId ?? null;
  return useQuery<string>({
    queryKey: ['video-url', gsUri, learningPropertyId],
    enabled: !!gsUri,
    staleTime: 45 * 60 * 1000, // under the Firebase ID token TTL (~1h)
    gcTime: 45 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const token = await firebaseAuth.currentUser?.getIdToken();
      if (!token) throw new Error('Not signed in');
      const uri = encodeURIComponent(gsUri!);
      const t = encodeURIComponent(token);
      return learningPropertyId
        ? `${API_URL}/v1/learning/video?uri=${uri}&propertyId=${encodeURIComponent(learningPropertyId)}&token=${t}`
        : `${API_URL}/v1/qa-video?uri=${uri}&token=${t}`;
    },
  });
}
